import { useMemo } from "react";
import { IBarData } from "apps/ai-metrics/types/aiMetrics.types";
import {
  createGroupedBarOption,
  createStackedBarOption,
  comparisonColorAt,
  COMPARISON_COLORS,
  SERIES_COLORS,
} from "apps/ai-metrics/utils/echartOptions";
import { SectionChart } from "apps/ai-metrics/components/shared/SectionChart/SectionChart";
import { SectionHeader } from "apps/ai-metrics/components/shared/SectionHeader/SectionHeader";
import { StatCard } from "apps/ai-metrics/components/shared/StatCard/StatCard";
import { useSectionStyles as useStyles } from "apps/ai-metrics/components/shared/useSectionStyles";
import {
  SECTION_NOTES,
  getMetricHelp,
  getChartHelp,
} from "apps/ai-metrics/config/metricHelpText";
import {
  AdaptedVelocity,
  ISprintChartData,
} from "apps/ai-metrics/utils/dashboardAdapter";

const CARD_META = [
  {
    label: "THROUGHPUT",
    subtitle: "Work items closed",
    key: "throughput" as const,
    higherIsBetter: true,
  },
  {
    label: "COMMITMENT RELIABILITY",
    subtitle: "% of sprint-committed items closed",
    key: "commitmentReliability" as const,
    higherIsBetter: true,
  },
  {
    label: "CYCLE TIME",
    subtitle: "Time from Active → Resolved (business days)",
    key: "cycleTime" as const,
    higherIsBetter: false,
  },
  {
    label: "COMPLETED WORK / 1-EFFORT ITEM",
    subtitle: "Avg hours to complete one effort point",
    key: "workPerPerson" as const,
    higherIsBetter: false,
  },
  {
    label: "CARRY-OVER RATE",
    subtitle: "Items not finished in planned sprint",
    key: "carryOverRate" as const,
    higherIsBetter: false,
  },
];

const CHART_Y_LABEL = "Items Closed";
const CYCLE_TIME_Y_LABEL = "Days";
const CYCLE_TIME_VALUE_DECIMALS = 1;

const velocityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const percentChange = (
  current?: number,
  prior?: number,
): number | undefined => {
  if (current === undefined || prior === undefined || prior === 0) {
    return undefined;
  }
  return Math.round(((current - prior) / prior) * 100);
};

const roundDays = (value: number | null): number | null =>
  typeof value === "number"
    ? Number(value.toFixed(CYCLE_TIME_VALUE_DECIMALS))
    : value;

const formatSprintLabels = (labels: string[]): string[] => {
  const years = new Set(
    labels.map((l) => l?.slice(0, 4)).filter((y): y is string => !!y),
  );
  const multiYear = years.size > 1;
  return labels.map((label) => {
    if (!label) return label;
    const match = /^(\d{4})-(\d{2})$/.exec(label);
    if (!match) return label;
    const [, year, month] = match;
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString(undefined, {
      month: "short",
      ...(multiYear ? { year: "numeric" } : {}),
    });
  });
};

interface IVelocitySectionProps {
  kpis?: AdaptedVelocity;
  loading?: boolean;
  error?: boolean;
  throughputLoading?: boolean;
  comparisonEnabled: boolean;
  teamCount: number;
  velocity?: number;
  velocityPrior?: number;
  velocityLoading?: boolean;
  velocityError?: boolean;
  throughputSprintData?: ISprintChartData;
  throughputComparisonData?: IBarData;
  cycleTimeSprintData?: ISprintChartData;
  cycleTimeComparisonData?: IBarData;
  cycleTimeLoading?: boolean;
  cycleTimeError?: boolean;
}

export const VelocitySection = ({
  kpis,
  loading,
  error,
  throughputLoading,
  comparisonEnabled,
  teamCount,
  velocity,
  velocityPrior,
  velocityLoading = false,
  velocityError = false,
  throughputSprintData,
  throughputComparisonData,
  cycleTimeSprintData,
  cycleTimeComparisonData,
  cycleTimeLoading = false,
  cycleTimeError = false,
}: IVelocitySectionProps): JSX.Element => {
  const styles = useStyles();

  const velocityValue =
    velocity === undefined ? undefined : velocityFormatter.format(velocity);
  const velocityDelta = comparisonEnabled
    ? percentChange(velocity, velocityPrior)
    : undefined;

  const hasData = comparisonEnabled
    ? !!throughputComparisonData
    : !!throughputSprintData;

  const chartOption = (() => {
    if (comparisonEnabled && throughputComparisonData) {
      return createGroupedBarOption({
        xLabels: throughputComparisonData.labels,
        series: throughputComparisonData.series.map((s, i) => ({
          name: s.name,
          data: s.values,
          color: comparisonColorAt(i, throughputComparisonData.series.length),
        })),
        yAxisLabel: CHART_Y_LABEL,
      });
    }

    if (!comparisonEnabled && throughputSprintData) {
      if (teamCount > 1) {
        return createStackedBarOption({
          xLabels: throughputSprintData.labels,
          series: throughputSprintData.teams.map((t, i) => ({
            name: t.team,
            data: t.current,
            color: SERIES_COLORS[i % SERIES_COLORS.length],
          })),
          yAxisLabel: CHART_Y_LABEL,
        });
      }
      const team = throughputSprintData.teams[0];
      return createGroupedBarOption({
        xLabels: throughputSprintData.labels,
        series: team
          ? [
              {
                name: team.team,
                data: team.current,
                color: COMPARISON_COLORS.current,
              },
            ]
          : [],
        yAxisLabel: CHART_Y_LABEL,
      });
    }

    return undefined;
  })();

  const hasCycleTimeData = comparisonEnabled
    ? !!cycleTimeComparisonData
    : !!cycleTimeSprintData;

  const cycleTimeOption = useMemo(() => {
    if (comparisonEnabled && cycleTimeComparisonData) {
      return createGroupedBarOption({
        xLabels: cycleTimeComparisonData.labels,
        series: cycleTimeComparisonData.series.map((s, i) => ({
          name: s.name,
          data: s.values.map(roundDays),
          color: comparisonColorAt(i, cycleTimeComparisonData.series.length),
        })),
        yAxisLabel: CYCLE_TIME_Y_LABEL,
      });
    }

    if (!comparisonEnabled && cycleTimeSprintData) {
      const xLabels = formatSprintLabels(cycleTimeSprintData.labels);
      if (teamCount > 1) {
        return createStackedBarOption({
          xLabels,
          series: cycleTimeSprintData.teams.map((t, i) => ({
            name: t.team,
            data: t.current.map(roundDays),
            color: SERIES_COLORS[i % SERIES_COLORS.length],
          })),
          yAxisLabel: CYCLE_TIME_Y_LABEL,
        });
      }
      const team = cycleTimeSprintData.teams[0];
      return createGroupedBarOption({
        xLabels,
        series: team
          ? [
              {
                name: team.team,
                data: team.current.map(roundDays),
                color: COMPARISON_COLORS.current,
              },
            ]
          : [],
        yAxisLabel: CYCLE_TIME_Y_LABEL,
      });
    }

    return undefined;
  }, [
    comparisonEnabled,
    cycleTimeComparisonData,
    cycleTimeSprintData,
    teamCount,
  ]);

  return (
    <div className={styles.section}>
      <SectionHeader
        title="Velocity & Throughput"
        note={SECTION_NOTES.velocity}
      >
        <div className={styles.cardsRow}>
          {CARD_META.map(({ label, subtitle, key, higherIsBetter }) => (
            <StatCard
              key={label}
              label={label}
              subtitle={subtitle}
              higherIsBetter={higherIsBetter}
              loading={loading}
              {...(!loading ? (kpis?.[key] ?? { pending: true }) : {})}
              info={getMetricHelp(label)}
            />
          ))}
          <StatCard
            label="VELOCITY"
            subtitle="Effort (story points) completed"
            value={velocityValue}
            delta={velocityDelta}
            higherIsBetter
            loading={velocityLoading}
            pending={
              !velocityLoading && !velocityError && velocity === undefined
            }
            info={getMetricHelp("VELOCITY")}
          />
        </div>
        <SectionChart
          title="Throughput by Team"
          option={
            chartOption
              ? { ...chartOption, legend: { show: false } }
              : undefined
          }
          loading={throughputLoading}
          emptyMessage={
            !throughputLoading && !hasData
              ? error
                ? "Failed to load data"
                : "No data for selected period"
              : undefined
          }
          info={getChartHelp("Throughput by Team")}
        />
        <SectionChart
          title="Avg Resolution Days (Cycle Time)"
          option={
            cycleTimeOption
              ? { ...cycleTimeOption, legend: { show: false } }
              : undefined
          }
          loading={cycleTimeLoading}
          emptyMessage={
            !cycleTimeLoading && !hasCycleTimeData
              ? cycleTimeError
                ? "Failed to load data"
                : "No data for selected period"
              : undefined
          }
          info={getChartHelp("Avg Resolution Days (Cycle Time)")}
        />
      </SectionHeader>
    </div>
  );
};
